<script setup>
import CustomDataTable from '@/dashboard/components/CustomDataTable.vue';

const props = defineProps({
  error: {
    type: String,
    default: null
  },
  missingColumnsWarning: {
    type: String,
    default: null
  },
  productTypeValidationWarning: {
    type: String,
    default: null
  },
  filteredRowsWarning: {
    type: String,
    default: null
  },
  importSummary: {
    type: String,
    default: null
  },
  filteredCsvHeaders: {
    type: Array,
    required: true
  },
  filteredCsvRows: {
    type: Array,
    required: true
  },
  selectedInboxes: {
    type: Array,
    required: true
  },
  inboxOptions: {
    type: Array,
    default: () => []
  },
  showInboxDropdown: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  'toggle-inbox-dropdown',
  'close-inbox-dropdown',
  'toggle-inbox-selection'
]);

const getSelectedInboxDisplayText = () => {
  if (props.selectedInboxes.includes('todos')) {
    return 'Todos os inboxes';
  } else if (props.selectedInboxes.includes('nenhum')) {
    return 'Nenhum inbox';
  } else if (props.selectedInboxes.length === 0) {
    return 'Selecionar inboxes';
  } else if (props.selectedInboxes.length === 1) {
    const inbox = props.inboxOptions.find(inbox => 
      String(inbox.inbox_id) === props.selectedInboxes[0]
    );
    return inbox ? (inbox.inbox_name || inbox.name) : 'Inbox desconhecido';
  } else {
    return `${props.selectedInboxes.length} inboxes selecionados`;
  }
};

const getSelectedInboxDisplayIcon = () => {
  if (props.selectedInboxes.includes('todos')) {
    return 'i-lucide-globe';
  } else if (props.selectedInboxes.includes('nenhum')) {
    return 'i-lucide-x-circle';
  } else if (props.selectedInboxes.length === 0) {
    return 'i-lucide-inbox';
  } else if (props.selectedInboxes.length === 1) {
    const inbox = props.inboxOptions.find(inbox => 
      String(inbox.inbox_id) === props.selectedInboxes[0]
    );
    return inbox ? getInboxTypeIcon(inbox.channel_type) : 'i-lucide-inbox';
  } else {
    return 'i-lucide-layers';
  }
};

const getInboxTypeIcon = (channelType) => {
  switch (channelType) {
    case 'Channel::WhatsApp':
      return 'i-lucide-message-circle';
    case 'Channel::Telegram':
      return 'i-lucide-send';
    case 'Channel::Email':
      return 'i-lucide-mail';
    default:
      return 'i-lucide-inbox';
  }
};
</script>

<template>
  <div class="p-6">
    <!-- Error message -->
    <div v-if="error" class="bg-n-ruby-1 dark:bg-n-ruby-3 p-4 rounded-lg border border-n-ruby-4 dark:border-n-ruby-6 mb-4">
      <div class="flex items-start">
        <i class="i-lucide-alert-triangle text-n-ruby-11 mt-1 mr-2"></i>
        <p class="text-sm text-n-ruby-11">{{ error }}</p>
      </div>
    </div>
    
    <!-- Single status message (error/warning/summary) -->
    <div v-if="missingColumnsWarning || productTypeValidationWarning || filteredRowsWarning || importSummary" class="mb-4">
      <!-- Missing columns error (fatal - no summary) -->
      <div v-if="missingColumnsWarning" class="bg-ruby-50 dark:bg-ruby-100 p-4 rounded-lg border border-ruby-600">
        <div class="flex items-center">
          <i class="i-lucide-alert-triangle text-n-ruby-11 mr-2 text-2xl"></i>
          <div>
            <p class="text-base font-medium text-n-ruby-11 m-0">Erro na planilha</p>
            <p class="text-sm text-n-slate-11">{{ missingColumnsWarning }}</p>
          </div>
        </div>
      </div>
      
      <!-- Product type validation error (fatal - no summary) -->
      <div v-else-if="productTypeValidationWarning" class="bg-ruby-50 dark:bg-ruby-100 p-4 rounded-lg border border-ruby-600">
        <div class="flex items-center">
          <i class="i-lucide-alert-triangle text-n-ruby-11 mr-2 text-2xl"></i>
          <div>
            <p class="text-base font-medium text-n-ruby-11 m-0">Erro nos tipos de produto</p>
            <p class="text-sm text-n-slate-11">{{ productTypeValidationWarning }}</p>
          </div>
        </div>
      </div>
      
      <!-- Filtered rows warning with summary -->
      <div v-else-if="filteredRowsWarning" class="bg-amber-50 dark:bg-amber-100 p-4 rounded-lg border border-amber-600">
        <div class="flex items-center">
          <i class="i-lucide-alert-triangle text-n-amber-11 mr-2 text-3xl"></i>
          <div>
            <p class="text-base font-medium text-n-amber-11 mb-1">Linhas ignoradas</p>
            <p class="text-sm text-n-slate-11 mb-2">{{ filteredRowsWarning }} {{ importSummary }}</p>
          </div>
        </div>
      </div>
      
      <!-- Import summary only (when no warnings or errors) -->
      <div v-else-if="importSummary" class="bg-woot-50 dark:bg-woot-900 p-4 rounded-lg border border-woot-600">
        <div class="flex items-center">
          <i class="i-lucide-info text-n-brand mr-2 text-2xl"></i>
          <div>
            <p class="text-base font-medium text-n-brand m-0">Resumo da importação</p>
            <p class="text-sm text-n-slate-11">{{ importSummary }}</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- CSV Preview Table -->
    <div class="mb-6">
      <h4 class="text-sm font-medium text-n-slate-12 mb-3">
        <span v-if="filteredCsvRows.length > 5">
          Apenas 5 das {{ filteredCsvRows.length }} linhas estão sendo exibidas na pré-visualização
        </span>
        <span v-else>
          Todas as {{ filteredCsvRows.length }} linhas estão sendo exibidas na pré-visualização
        </span>
      </h4>
      <CustomDataTable
        v-if="filteredCsvRows.length"
        :headers="filteredCsvHeaders"
        :rows="filteredCsvRows.slice(0, 5)"
        :selection-config="{ enabled: false }"
        :hover-config="{
          borderColor: 'border-n-slate-4',
          backgroundColor: 'bg-n-slate-1 dark:bg-n-slate-3',
          textColor: ''
        }"
        :border-config="{
          default: 'border-n-slate-3 dark:border-n-slate-7',
          hover: 'border-n-slate-4 dark:border-n-slate-6',
          selected: 'border-n-slate-4 dark:border-n-slate-6'
        }"
        :text-truncation="{
          enabled: true,
          maxLength: 30,
          showExpandButton: false
        }"
        max-height="max-h-64"
      />
    </div>
    
    <!-- Bottom container with gray background and inbox selection -->
    <div class="p-4 bg-n-slate-1 rounded-lg border border-n-slate-3 dark:border-n-slate-7">
      <div class="flex items-center justify-between">
        <div class="text-sm font-medium text-n-slate-12">
          Inbox para selecionar:
        </div>
        
        <!-- Inbox Dropdown -->
        <div class="relative ml-4 flex-1 max-w-sm">
          <button
            @click.stop="emit('toggle-inbox-dropdown', $event)"
            v-on-clickaway="() => emit('close-inbox-dropdown')"
            class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-transparent rounded-lg text-sm bg-n-background dark:bg-n-slate-3 text-n-slate-12 focus:border-n-brand dark:focus:border-n-brand hover:bg-n-slate-1 dark:hover:bg-n-slate-4 flex justify-between items-center text-left transition-all duration-200"
            type="button"
          >
            <div class="flex items-center overflow-hidden">
              <div class="w-6 h-6 flex items-center justify-center rounded-full mr-2 flex-shrink-0 bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                <i :class="getSelectedInboxDisplayIcon() + ' text-sm'"></i>
              </div>
              <span class="truncate">
                {{ getSelectedInboxDisplayText() }}
              </span>
            </div>
            <i class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform" :class="{ 'rotate-180': showInboxDropdown }"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>